import React from 'react';
import {
  Box,
  Typography,
  Divider,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemText,
  Button,
  Chip,
  Stack,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useFormContext } from './FormContext';

interface SectionEditProps {
  title: string;
  sectionIndex: number;
  onEdit: (section: number) => void;
}

const SectionEdit: React.FC<SectionEditProps> = ({ title, sectionIndex, onEdit }) => (
  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
    <Typography variant="h6" component="h2" color="primary">
      {title}
    </Typography>
    <IconButton 
      color="primary"
      onClick={() => onEdit(sectionIndex)}
      aria-label={`Editar seção ${title}`}
    >
      <EditIcon />
    </IconButton>
  </Box>
);

interface Page6ReviewProps {
  onEditSection: (section: number) => void;
}

const Page6Review: React.FC<Page6ReviewProps> = ({ onEditSection }) => {
  const { formData } = useFormContext();

  const getEmojiUsageText = (value: string): string => {
    switch (value) {
      case 'yes': return 'Sim, usar emojis para deixar a conversa mais leve';
      case 'moderate': return 'Usar com moderação, apenas quando fizer sentido';
      case 'no': return 'Não usar emojis';
      default: return 'Não especificado';
    }
  };

  const getClientAddressingText = (value: string): string => {
    switch (value) {
      case 'informal': return 'Usando "você" (informal)';
      case 'formal': return 'Usando "o senhor / a senhora" (formal)';
      default: return 'Não especificado';
    }
  };

  const getMainObjectiveText = (value: string): string => {
    switch (value) {
      case 'answer_faqs': return 'Responder perguntas frequentes';
      case 'qualify_clients': return 'Qualificar clientes antes de transferir';
      case 'schedule_appointments': return 'Agendar horários/consultas';
      case 'sell_products': return 'Apresentar produtos/serviços e fechar vendas';
      default: return 'Não especificado';
    }
  };

  const getTransferConditionText = (value: string): string => {
    switch (value) {
      case 'request_human': return 'Cliente pedir para falar com humano';
      case 'upset_customer': return 'Cliente irritado ou insatisfeito';
      case 'price_negotiation': return 'Negociação de preços/descontos';
      case 'complex_sale': return 'Venda complexa';
      case 'specific_question': return 'Pergunta muito específica';
      default: return value;
    }
  };

  return (
    <>
      <Typography variant="h5" gutterBottom sx={{ mt: 2, mb: 3, color: "primary.main", textAlign: 'center' }}>
        Revisão do Briefing
      </Typography>
      <Typography variant="body1" gutterBottom sx={{ mb: 4, textAlign: 'center' }}>
        Revise as informações fornecidas antes de finalizar. Clique no ícone de edição para modificar qualquer seção.
      </Typography>

      {/* Seção 1: Identidade da Empresa */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <SectionEdit 
          title="Identidade da Empresa" 
          sectionIndex={0} 
          onEdit={onEditSection} 
        />
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Nome da Empresa:</Typography>
            <Typography variant="body1" gutterBottom>{formData.businessName}</Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Ramo de Atividade:</Typography>
            <Typography variant="body1" gutterBottom>{formData.businessIndustry}</Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Descrição do Negócio:</Typography>
            <Typography variant="body1" gutterBottom>{formData.businessDescription}</Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Público-alvo:</Typography>
            <Typography variant="body1" gutterBottom>{formData.targetAudience || "Não especificado"}</Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Diferenciais da Empresa:</Typography>
            <Typography variant="body1" gutterBottom>{formData.businessDifferentials}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Seção 2: Personalidade do Assistente */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <SectionEdit 
          title="Personalidade do Assistente" 
          sectionIndex={1} 
          onEdit={onEditSection} 
        />
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Nome do Assistente:</Typography>
            <Typography variant="body1" gutterBottom>{formData.assistantName || "Não especificado"}</Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Tom de Voz:</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
              {formData.toneOfVoice.map((tone, index) => (
                <Chip 
                  key={index} 
                  label={tone === 'super_friendly' ? 'Super amigável' : 
                         tone === 'friendly' ? 'Amigável' :
                         tone === 'professional' ? 'Profissional' :
                         tone === 'enthusiastic' ? 'Entusiasmado' :
                         tone === 'caring' ? 'Acolhedor' : tone} 
                  size="small" 
                  color="primary" 
                  variant="outlined" 
                />
              ))}
            </Stack>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Uso de Emojis:</Typography>
            <Typography variant="body1" gutterBottom>{getEmojiUsageText(formData.emojiUsage)}</Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Forma de Tratamento:</Typography>
            <Typography variant="body1" gutterBottom>{getClientAddressingText(formData.clientAddressing)}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Seção 3: Conhecimento Essencial */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <SectionEdit 
          title="Conhecimento Essencial" 
          sectionIndex={2} 
          onEdit={onEditSection} 
        />
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Produtos/Serviços:</Typography>
            <List dense>
              {formData.productsServices.map((item, index) => (
                <ListItem key={index} sx={{ pl: 0 }}>
                  <ListItemText
                    primary={`${item.name} - ${item.price}`}
                    secondary={item.description}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Horário de Funcionamento:</Typography>
            <Typography variant="body1" gutterBottom>{formData.workingHours}</Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Localização:</Typography>
            <Typography variant="body1" gutterBottom>
              {formData.businessLocation || "Não especificado"}
              {formData.googleMapsLink && <span> (Link Maps disponível)</span>}
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Contato:</Typography>
            <Typography variant="body1">
              {formData.phoneContact && `Telefone: ${formData.phoneContact}`}
              {formData.emailContact && <span>{formData.phoneContact ? ' | ' : ''}Email: {formData.emailContact}</span>}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {formData.websiteUrl && `Site: ${formData.websiteUrl}`}
              {formData.socialMedia && <span>{formData.websiteUrl ? ' | ' : ''}Redes Sociais: {formData.socialMedia}</span>}
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Pagamento:</Typography>
            <Typography variant="body1" gutterBottom>{formData.paymentMethods || "Não especificado"}</Typography>
          </Grid>
          
          {(formData.deliveryAreas || formData.deliveryFee || formData.deliveryTime) && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Entrega:</Typography>
              <Typography variant="body1">
                {formData.deliveryAreas && `Áreas: ${formData.deliveryAreas}`}
                {formData.deliveryFee && <span>{formData.deliveryAreas ? ' | ' : ''}Taxa: ${formData.deliveryFee}</span>}
                {formData.deliveryTime && <span>{formData.deliveryAreas || formData.deliveryFee ? ' | ' : ''}Prazo: ${formData.deliveryTime}</span>}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Seção 4: Objetivos e Ações */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <SectionEdit 
          title="Objetivos e Ações" 
          sectionIndex={3} 
          onEdit={onEditSection} 
        />
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Objetivo Principal:</Typography>
            <Typography variant="body1" gutterBottom>{getMainObjectiveText(formData.mainObjective)}</Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Perguntas Frequentes:</Typography>
            <List dense>
              {formData.faqs.map((faq, index) => (
                <ListItem key={index} alignItems="flex-start" sx={{ pl: 0 }}>
                  <ListItemText
                    primary={`Pergunta ${index + 1}: ${faq.question}`}
                    secondary={
                      <React.Fragment>
                        <Typography variant="body2" color="text.secondary">Resposta:</Typography>
                        {faq.answer}
                      </React.Fragment>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Captura de Leads:</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
              {formData.leadCapture.name && <Chip label="Nome" size="small" />}
              {formData.leadCapture.phone && <Chip label="Telefone" size="small" />}
              {formData.leadCapture.email && <Chip label="Email" size="small" />}
              {formData.leadCapture.interest && <Chip label="Interesse" size="small" />}
            </Stack>
            {formData.leadCapture.other && (
              <Typography variant="body2">Outro: {formData.leadCapture.other}</Typography>
            )}
          </Grid>
          
          {formData.appointmentTool && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Ferramenta de Agendamento:</Typography>
              <Typography variant="body1" gutterBottom>{formData.appointmentTool}</Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Seção 5: Limites e Escalonamento */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <SectionEdit 
          title="Limites e Escalonamento" 
          sectionIndex={4} 
          onEdit={onEditSection} 
        />
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={2}>
          {formData.forbiddenTopics && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Tópicos Proibidos:</Typography>
              <Typography variant="body1" gutterBottom>{formData.forbiddenTopics}</Typography>
            </Grid>
          )}
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Condições para Transferência:</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
              {formData.transferConditions.map((condition, index) => (
                <Chip 
                  key={index} 
                  label={getTransferConditionText(condition)} 
                  size="small" 
                  color="primary" 
                  variant="outlined" 
                />
              ))}
            </Stack>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Mensagem de Transferência:</Typography>
            <Typography variant="body1" gutterBottom>{formData.transferMessage}</Typography>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

export default Page6Review;